import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Zap, CreditCard, Check, ChevronRight, Smartphone, Download, Bell } from 'lucide-react';
import { SERVICE_CATEGORIES } from '../constants/categories';

const Welcome = () => {
  const navigate = useNavigate();
  const { language, updateLanguage } = useContext(AuthContext);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const content = {
    en: {
      title: 'Helply',
      heroTitle: 'Find Service Professionals Across Switzerland',
      heroDesc: 'Find electricians, painters, cleaners, drivers, babysitters, pet sitters and other service professionals across Switzerland.',
      getStarted: 'Get Started',
      features: [
        { icon: Shield, title: 'Helply Verified Badge', desc: 'The badge confirms Helply reviewed submitted identity and professional documents' },
        { icon: Zap, title: 'Fast Booking', desc: 'Get help within hours' },
        { icon: CreditCard, title: 'Secure Payments', desc: 'Pay safely with Worldline' }
      ],
      installApp: 'Install Our App',
      installDesc: 'Scan the QR code with your phone camera to install Helply on your home screen. Access all features with one tap!',
      availableOn: 'Available on',
      notifications: 'Get instant notifications',
      pointCamera: 'Point your camera at the QR code',
      categoriesTitle: '26 Service Areas',
      categoriesDesc: 'Professional services for all your daily needs',
      viewAll: 'View all 26 categories',
      viewLess: 'Show less',
      pricingTitle: 'Pricing Plans',
      pricingDesc: 'Choose your yearly subscription',
      clientPlan: 'Client',
      clientDesc: 'For those seeking services',
      clientPrice: 'CHF 12',
      clientMonthly: '= CHF 1.00/month',
      providerPlan: 'Service Provider',
      providerDesc: 'For professionals offering services',
      providerPrice: 'CHF 99',
      providerMonthly: '= CHF 8.25/month',
      perYear: '/year',
      clientFeatures: ['Post unlimited jobs', 'Chat with professionals', 'Secure payments', 'Provider profiles'],
      providerFeatures: ['Unlimited job applications', 'Eligibility for verified badge', 'Priority in search', 'Direct messaging'],
      startNow: 'Start Now',
      ctaTitle: 'Start Today',
      ctaDesc: 'Your comfort and convenience come first—every day. Download your Helply Swiss App today.',
      registerFree: 'Register Free',
      bannerLine1: 'Your Trusted',
      bannerLine2highlight: 'Swiss',
      bannerLine2rest: 'Service Marketplace',
      bannerSub: 'Providers with a Helply verified badge have completed our identity and document review.'
    },
    de: {
      title: 'Helply',
      heroTitle: 'Finden Sie Dienstleister in der ganzen Schweiz',
      heroDesc: 'Finden Sie Elektriker, Maler, Reinigungskräfte, Fahrer, Babysitter, Tiersitter und weitere Dienstleister in der ganzen Schweiz.',
      getStarted: 'Jetzt Starten',
      features: [
        { icon: Shield, title: 'Helply-Verifizierungsabzeichen', desc: 'Das Abzeichen bestätigt die Prüfung eingereichter Identitäts- und Berufsdokumente durch Helply' },
        { icon: Zap, title: 'Schnelle Buchung', desc: 'Erhalten Sie Hilfe innerhalb von Stunden' },
        { icon: CreditCard, title: 'Sichere Zahlungen', desc: 'Bezahlen Sie sicher mit Worldline' }
      ],
      installApp: 'Installieren Sie unsere App',
      installDesc: 'Scannen Sie den QR-Code mit Ihrer Handykamera, um Helply auf Ihrem Startbildschirm zu installieren. Greifen Sie mit einem Tipp auf alle Funktionen zu!',
      availableOn: 'Verfügbar auf',
      notifications: 'Sofortige Benachrichtigungen erhalten',
      pointCamera: 'Richten Sie Ihre Kamera auf den QR-Code',
      categoriesTitle: '26 Bereiche',
      categoriesDesc: 'Professionelle Dienstleistungen für alle Ihre täglichen Bedürfnisse',
      viewAll: 'Alle 26 Bereiche ansehen',
      viewLess: 'Weniger anzeigen',
      pricingTitle: 'Preisübersicht',
      pricingDesc: 'Wählen Sie Ihr Jahresabonnement',
      clientPlan: 'Kunde',
      clientDesc: 'Für Dienstleistungssuchende',
      clientPrice: 'CHF 12',
      clientMonthly: '= CHF 1.00/Monat',
      providerPlan: 'Dienstleister',
      providerDesc: 'Für Fachleute, die Dienstleistungen anbieten',
      providerPrice: 'CHF 99',
      providerMonthly: '= CHF 8.25/Monat',
      perYear: '/Jahr',
      clientFeatures: ['Unbegrenzt Jobs posten', 'Mit Profis chatten', 'Sichere Zahlungen', 'Anbieterprofile'],
      providerFeatures: ['Unbegrenzte Bewerbungen', 'Berechtigung für das Verifizierungsabzeichen', 'Priorität in der Suche', 'Direkte Nachrichten'],
      startNow: 'Jetzt Starten',
      ctaTitle: 'Starten Sie jetzt',
      ctaDesc: 'Ihr Komfort und Ihre Bequemlichkeit stehen im Mittelpunkt – jeden Tag. Laden Sie Ihre Helply Swiss App noch heute herunter.',
      registerFree: 'Kostenlos Registrieren',
      bannerLine1: 'Ihr vertrauensvoller',
      bannerLine2highlight: 'Schweizer',
      bannerLine2rest: 'Dienstleistungsmarkt',
      bannerSub: 'Das Helply-Verifizierungsabzeichen bestätigt die Prüfung der eingereichten Identitäts- und Berufsdokumente.'
    },
    it: {
      title: 'Helply',
      heroTitle: 'Trova professionisti dei servizi in tutta la Svizzera',
      heroDesc: 'Trova elettricisti, imbianchini, addetti alle pulizie, autisti, babysitter, pet sitter e altri professionisti in tutta la Svizzera.',
      getStarted: 'Inizia Ora',
      features: [
        { icon: Shield, title: 'Badge verificato Helply', desc: 'Il badge conferma la revisione da parte di Helply dei documenti d’identità e professionali inviati' },
        { icon: Zap, title: 'Prenotazione Rapida', desc: 'Ricevi aiuto in poche ore' },
        { icon: CreditCard, title: 'Pagamenti Sicuri', desc: 'Paga in sicurezza con Worldline' }
      ],
      installApp: 'Installa la nostra App',
      installDesc: 'Scansiona il codice QR con la fotocamera del telefono per installare Helply sulla tua schermata principale. Accedi a tutte le funzionalità con un tocco!',
      availableOn: 'Disponibile su',
      notifications: 'Ricevi notifiche istantanee',
      pointCamera: 'Punta la fotocamera sul codice QR',
      categoriesTitle: '26 Categorie',
      categoriesDesc: 'Servizi professionali per tutte le tue esigenze quotidiane',
      viewAll: 'Vedi tutte le 26 categorie',
      viewLess: 'Mostra meno',
      pricingTitle: 'Piani Tariffari',
      pricingDesc: 'Scegli il tuo abbonamento annuale',
      clientPlan: 'Cliente',
      clientDesc: 'Per chi cerca servizi',
      clientPrice: 'CHF 12',
      clientMonthly: '= CHF 1.00/mese',
      providerPlan: 'Fornitore di Servizi',
      providerDesc: 'Per professionisti che offrono servizi',
      providerPrice: 'CHF 99',
      providerMonthly: '= CHF 8.25/mese',
      perYear: '/anno',
      clientFeatures: ['Pubblica lavori illimitati', 'Chatta con professionisti', 'Pagamenti sicuri', 'Profili dei fornitori'],
      providerFeatures: ['Candidature illimitate', 'Idoneità al badge verificato', 'Priorità nella ricerca', 'Messaggistica diretta'],
      startNow: 'Inizia Ora',
      ctaTitle: 'Inizia Oggi',
      ctaDesc: 'Il tuo comfort e la tua comodità vengono prima di tutto, ogni giorno. Scarica la tua App Helply Swiss oggi.',
      registerFree: 'Registrati Gratis',
      bannerLine1: 'Il Vostro Fidato',
      bannerLine2highlight: 'Mercato',
      bannerLine2rest: 'dei Servizi Svizzero',
      bannerSub: 'Il badge verificato Helply conferma la revisione dei documenti d’identità e professionali inviati.'
    },
    fr: {
      title: 'Helply',
      heroTitle: 'Trouvez des professionnels dans toute la Suisse',
      heroDesc: 'Trouvez des électriciens, peintres, agents de nettoyage, chauffeurs, baby-sitters, pet-sitters et autres professionnels dans toute la Suisse.',
      getStarted: 'Commencer',
      features: [
        { icon: Shield, title: 'Badge vérifié Helply', desc: 'Le badge confirme l’examen par Helply des documents d’identité et professionnels soumis' },
        { icon: Zap, title: 'Réservation Rapide', desc: 'Obtenez de l\'aide en quelques heures' },
        { icon: CreditCard, title: 'Paiements Sécurisés', desc: 'Payez en toute sécurité avec Worldline' }
      ],
      installApp: 'Installer notre App',
      installDesc: 'Scannez le code QR avec l\'appareil photo de votre téléphone pour installer Helply sur votre écran d\'accueil. Accédez à toutes les fonctionnalités en un clic !',
      availableOn: 'Disponible sur',
      notifications: 'Recevez des notifications instantanées',
      pointCamera: 'Pointez votre appareil photo vers le code QR',
      categoriesTitle: '26 Catégories',
      categoriesDesc: 'Services professionnels pour tous vos besoins quotidiens',
      viewAll: 'Voir les 26 catégories',
      viewLess: 'Voir moins',
      pricingTitle: 'Plans Tarifaires',
      pricingDesc: 'Choisissez votre abonnement annuel',
      clientPlan: 'Client',
      clientDesc: 'Pour ceux qui recherchent des services',
      clientPrice: 'CHF 12',
      clientMonthly: '= CHF 1.00/mois',
      providerPlan: 'Prestataire de Services',
      providerDesc: 'Pour les professionnels offrant des services',
      providerPrice: 'CHF 99',
      providerMonthly: '= CHF 8.25/mois',
      perYear: '/an',
      clientFeatures: ['Publiez des emplois illimités', 'Chattez avec des professionnels', 'Paiements sécurisés', 'Profils des prestataires'],
      providerFeatures: ['Candidatures illimitées', 'Éligibilité au badge vérifié', 'Priorité dans la recherche', 'Messagerie directe'],
      startNow: 'Commencer',
      ctaTitle: 'Commencez Aujourd\'hui',
      ctaDesc: 'Votre confort et votre commodité passent en premier, chaque jour. Téléchargez votre App Helply Swiss dès aujourd\'hui.',
      registerFree: 'S\'inscrire Gratuitement',
      bannerLine1: 'Votre Marché de',
      bannerLine2highlight: 'Services Suisse',
      bannerLine2rest: 'de Confiance',
      bannerSub: 'Le badge vérifié Helply confirme l’examen des documents d’identité et professionnels soumis.'
    }
  };

  const t = content[language] || content.en;

  // Get category name based on language
  const getCategoryName = (cat) => {
    return language === 'en' ? cat.en : language === 'it' ? cat.it : language === 'fr' ? cat.fr : cat.de;
  };

  // Show first 10 categories or all
  const displayedCategories = showAllCategories ? SERVICE_CATEGORIES : SERVICE_CATEGORIES.slice(0, 10);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src="/helply-logo.jpg" alt="Helply Swiss logo" className="w-10 h-10 rounded-lg" width="40" height="40" />
            <span className="text-xl font-semibold text-gray-900">Helply Swiss</span>
          </div>
          <Select value={language} onValueChange={updateLanguage}>
            <SelectTrigger className="w-28 border-gray-200" data-testid="language-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="it">Italiano</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Hero Section — Language-specific banner images */}
      <section className="pt-16 relative w-full" data-testid="hero-section">
        <h1 className="sr-only" data-testid="homepage-heading">{t.heroTitle}</h1>
        <div className="relative w-full max-w-[1280px] mx-auto" style={{ aspectRatio: '16 / 9' }}>
          <img
            src={{
              en: '/helply-hero-en.webp',
              de: '/helply-hero-de.webp',
              fr: '/helply-hero-fr.webp',
              it: '/helply-hero-it.webp'
            }[language]}
            alt={`${t.heroTitle} — Helply Swiss`}
            data-testid="hero-promo-banner"
            width="1600"
            height="900"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div className="absolute inset-0 flex items-end justify-center pb-8"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.28) 0%, transparent 30%)', pointerEvents: 'none' }}>
            <Button
              size="lg"
              className="bg-blue-900 hover:bg-blue-800 text-white px-10 py-6 text-lg rounded-lg shadow-xl transition-all"
              onClick={() => navigate('/auth')}
              data-testid="get-started-btn"
              style={{ pointerEvents: 'auto' }}
            >
              {t.getStarted}
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 px-4 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {t.features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={idx}
                  className="flex items-start gap-4 p-6 rounded-xl hover:bg-gray-50 transition-colors"
                  data-testid={`feature-${idx}`}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Install App Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">
                {t.installApp}
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t.installDesc}
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-700">
                  <span className="font-medium">{t.availableOn}</span>
                </div>
                <div className="flex items-center gap-4">
                  {/* App Store Badge */}
                  <a href="https://apps.apple.com/ch/app/helply-swiss/id6760337245?l=en-GB" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                    <img 
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                      alt="Download Helply Swiss on the App Store" 
                      className="h-10"
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                  {/* Google Play Badge */}
                  <a href="https://play.google.com/store/apps/details?id=ch.helply.swiss" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                    <img 
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                      alt="Get Helply Swiss on Google Play" 
                      className="h-14 -my-2"
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-700 mt-2">
                  <Bell className="w-5 h-5 text-blue-900" />
                  <span>{t.notifications}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                <img 
                  src="/helply_qr_code.png" 
                  alt="QR code to install the Helply Swiss app on your phone" 
                  className="w-56 h-56"
                  width="224"
                  height="224"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="text-sm text-gray-500 mt-4">{t.pointCamera}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-2">{t.categoriesTitle}</h2>
            <p className="text-gray-600">{t.categoriesDesc}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {displayedCategories.map((cat, idx) => (
              <span 
                key={idx}
                className="px-4 py-2 bg-blue-50 text-blue-900 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors cursor-default"
              >
                {getCategoryName(cat)}
              </span>
            ))}
          </div>
          <div className="text-center mt-8">
            <button 
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="inline-flex items-center gap-2 text-blue-900 font-medium hover:underline"
            >
              {showAllCategories ? t.viewLess : t.viewAll}
              <ChevronRight className={`w-4 h-4 transition-transform ${showAllCategories ? 'rotate-90' : ''}`} />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-gray-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-white mb-2">{t.pricingTitle}</h2>
            <p className="text-gray-400">{t.pricingDesc}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Client Plan */}
            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-1">{t.clientPlan}</h3>
                <p className="text-gray-400 text-sm">{t.clientDesc}</p>
              </div>
              <div className="mb-2">
                <span className="text-4xl font-bold text-white">{t.clientPrice}</span>
                <span className="text-gray-400">{t.perYear}</span>
              </div>
              <p className="text-green-400 text-sm mb-6">{t.clientMonthly}</p>
              <ul className="space-y-3 mb-8">
                {t.clientFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-white text-gray-900 hover:bg-gray-100 py-6"
                onClick={() => navigate('/auth')}
              >
                {t.startNow}
              </Button>
            </div>

            {/* Service Provider Plan */}
            <div className="bg-blue-900 rounded-2xl p-8 border border-blue-700 relative">
              <div className="absolute -top-3 right-6 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                PRO
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-1">{t.providerPlan}</h3>
                <p className="text-blue-200 text-sm">{t.providerDesc}</p>
              </div>
              <div className="mb-2">
                <span className="text-4xl font-bold text-white">{t.providerPrice}</span>
                <span className="text-blue-200">{t.perYear}</span>
              </div>
              <p className="text-green-400 text-sm mb-6">{t.providerMonthly}</p>
              <ul className="space-y-3 mb-8">
                {t.providerFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-blue-100">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-white text-blue-900 hover:bg-blue-50 py-6 font-semibold"
                onClick={() => navigate('/auth')}
              >
                {t.startNow}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-gray-900 mb-4">{t.ctaTitle}</h2>
          <p className="text-gray-600 mb-8">{t.ctaDesc}</p>
          <Button 
            size="lg"
            className="bg-blue-900 hover:bg-blue-800 text-white px-10 py-6 text-lg rounded-lg"
            onClick={() => navigate('/auth')}
          >
            {t.registerFree}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900">
        {/* Top Banner Strip */}
        <div className="bg-blue-950 border-b border-blue-800">
          <div className="max-w-5xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/new-helply-logo.png" alt="Helply Swiss logo" className="h-9 w-auto" loading="lazy" decoding="async" />
              <div>
                <p className="text-white font-semibold text-sm">Helply Swiss</p>
                <p className="text-blue-300 text-xs">by GKV-Consulting GmbH</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-blue-300 text-xs">
                {language === 'de' ? 'Ihr Marktplatz für Fachkräfte in der Schweiz' :
                 language === 'it' ? 'Il vostro marketplace per professionisti in Svizzera' :
                 language === 'fr' ? 'Votre marketplace pour les professionnels en Suisse' :
                 'Your marketplace for skilled workers in Switzerland'}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Links & Copyright */}
        <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6 text-sm">
            <a href="/pricing/" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-pricing">
              {language === 'de' ? 'Preise' : language === 'it' ? 'Prezzi' : language === 'fr' ? 'Tarifs' : 'Pricing'}
            </a>
            <a href="/terms-policy/" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-imprint">
              {language === 'de' ? 'Impressum' : language === 'it' ? 'Informazioni' : language === 'fr' ? 'Mentions légales' : 'Imprint'}
            </a>
            <a href="/terms-policy/" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-terms">
              {language === 'de' ? 'AGB' : language === 'it' ? 'Termini' : language === 'fr' ? 'CGU' : 'Terms'}
            </a>
            <a href="/terms-policy/" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-privacy">
              {language === 'de' ? 'Datenschutz' : language === 'it' ? 'Privacy' : language === 'fr' ? 'Confidentialité' : 'Privacy'}
            </a>
          </div>
          <p className="text-gray-500 text-sm" data-testid="footer-copyright">
            © 2026 GKV-Consulting GmbH, Zürich
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Welcome;
